/**
 * Helper functions for the main App Reducer.
 */
export type ReducerTree<R, S, A> = {
  [K in keyof S]: ReducerTree<R, S[K], A> | Reducer<S[K], A>;
};

type Reducer<S, A> = (state: Readonly<S>, action: A) => Readonly<S>;

export function combineReducers<R, S, K extends keyof S, A>(reducers: ReducerTree<R, S, A>): Reducer<S, A> {
  const reducerKeys = Object.keys(reducers);
  const finalReducers: { [index: string]: Reducer<S[K], A> } = {};

  for (let i = 0; i < reducerKeys.length; i++) {
    const key = reducerKeys[i];
    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key] as Reducer<S[K], A>;
    } else if (typeof reducers[key] === 'object') {
      finalReducers[key] = combineReducers<R, S[K], keyof S[K], A>(reducers[key] as ReducerTree<R, S[K], A>);
    }
  }

  const finalReducerKeys = Object.keys(finalReducers);

  return function combination(state, action) {
    let hasChanged = false;
    const nextState = { ...(<any>state) };

    for (let i = 0; i < finalReducerKeys.length; i++) {
      const key = finalReducerKeys[i];
      const reducer = finalReducers[key];
      const previousStateForKey = nextState[key];

      const nextSubstate = reducer(nextState[key], action);
      hasChanged = hasChanged || nextSubstate !== previousStateForKey;

      if (hasChanged) {
        nextState[key] = nextSubstate;
      }
    }

    return hasChanged ? nextState : state;
  };
}

export function combineAllReducers<R, K extends keyof R, A>(reducers: ReducerTree<R, R, A>): (root: R, action: A) => R {
  const reducerKeys = Object.keys(reducers);
  const finalReducers: { [index: string]: Reducer<R[K], A> } = {};

  for (let i = 0; i < reducerKeys.length; i++) {
    const key = reducerKeys[i];
    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key] as Reducer<R[K], A>;
    } else if (typeof reducers[key] === 'object') {
      finalReducers[key] = combineReducers<R, R[K], keyof R[K], A>(reducers[key] as ReducerTree<R, R[K], A>);
    }
  }

  const finalReducerKeys = Object.keys(finalReducers);

  return function combination(root = <R>{}, action) {
    let hasChanged = false;

    // Generate new child states
    // Delta check all child state
    // If any child state is different, make a new root state, otherwise, return the existing state
    const nextState = { ...(<any>root) };

    for (let i = 0; i < finalReducerKeys.length; i++) {
      const key = finalReducerKeys[i];
      const reducer = finalReducers[key];
      const previousStateForKey = root[key];
      const nextStateForKey = reducer(nextState[key], action);

      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }

    return hasChanged ? nextState : root;
  };
}
